import apiClient from './api';

class AssetCategoryService {
  /**
   * Get all asset categories with optional filters
   * @param {Object} params - Query parameters {asset_category_value, is_active, search, ordering, page}
   * @returns {Promise} - List of asset categories
   */
  async getAssetCategories(params = {}) {
    try {
      const response = await apiClient.get('/admin/asset-category-values/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching asset categories:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get single asset category by ID
   * @param {string} id - Asset category ID
   * @returns {Promise} - Asset category details
   */
  async getAssetCategoryById(id) {
    try {
      const response = await apiClient.get(`/admin/asset-category-values/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching asset category:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create new asset category
   * @param {Object} data - {asset_category_value, asset_field_1_value, asset_field_2_value, asset_field_3_value, asset_field_4_value, is_active}
   * @returns {Promise} - Created asset category
   */
  async createAssetCategory(data) {
    try {
      const response = await apiClient.post('/admin/asset-category-values/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating asset category:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update asset category (full update)
   * @param {string} id - Asset category ID
   * @param {Object} data - Complete asset category data
   * @returns {Promise} - Updated asset category
   */
  async updateAssetCategory(id, data) {
    try {
      const response = await apiClient.put(`/admin/asset-category-values/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating asset category:', error);
      return this.handleError(error);
    }
  }

  /**
   * Partial update asset category
   * @param {string} id - Asset category ID
   * @param {Object} data - Partial asset category data
   * @returns {Promise} - Updated asset category
   */
  async patchAssetCategory(id, data) {
    try {
      const response = await apiClient.patch(`/admin/asset-category-values/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error patching asset category:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete asset category
   * @param {string} id - Asset category ID
   * @returns {Promise} - Success status
   */
  async deleteAssetCategory(id) {
    try {
      await apiClient.delete(`/admin/asset-category-values/${id}/`);

      return {
        success: true,
        message: 'Asset category deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting asset category:', error);
      return this.handleError(error);
    }
  }

  /**
   * Move asset category up in order
   * @param {string} id - Asset category ID
   * @returns {Promise} - Updated asset category
   */
  async moveUp(id) {
    try {
      const response = await apiClient.post(`/admin/asset-category-values/${id}/order/up/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error moving asset category up:', error);
      return this.handleError(error);
    }
  }

  /**
   * Move asset category down in order
   * @param {string} id - Asset category ID
   * @returns {Promise} - Updated asset category
   */
  async moveDown(id) {
    try {
      const response = await apiClient.post(`/admin/asset-category-values/${id}/order/down/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error moving asset category down:', error);
      return this.handleError(error);
    }
  }

  /**
   * Toggle active status of asset category
   * @param {string} id - Asset category ID
   * @returns {Promise} - Updated asset category
   */
  async toggleActive(id) {
    try {
      const response = await apiClient.post(`/admin/asset-category-values/${id}/toggle_active/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error toggling asset category active status:', error);
      return this.handleError(error);
    }
  }

  /**
   * Export asset categories to Excel
   * @returns {Promise} - Excel file blob
   */
  async exportToExcel() {
    try {
      const response = await apiClient.get('/admin/asset-category-values/export_excel/', {
        responseType: 'blob',
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error exporting asset categories to Excel:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      if (error.response.data) {
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else {
          // Try to get first error from field errors
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          }
        }
      }

      if (error.response.status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (error.response.status === 404) {
        message = 'Asset category not found.';
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new AssetCategoryService();
